import { useTranslation } from '@/hooks/useTranslation';
import { StringDropdownItem } from './dropdown/StringDropdown.types';
import StringDropdown from './dropdown/StringDropdown';
import { OffsetPageInfo, PageInfo, PaginationInfo } from 'graphql/generated';
import { ChevronLeftIcon, ChevronRightIcon } from '@/components/icons';
import { Pagination } from 'graphql/cms/generated';

type SupportedPageInfo =
  | PageInfo
  | Pagination
  | PaginationInfo
  | OffsetPageInfo;

function isPageInfo(
  pageInfo: SupportedPageInfo,
): pageInfo is PageInfo | OffsetPageInfo {
  return 'hasNextPage' in pageInfo && 'hasPreviousPage' in pageInfo;
}

function hasPage(pageInfo: SupportedPageInfo): pageInfo is PaginationInfo {
  return 'page' in pageInfo;
}

export const NumberPerPageDropdown = ({
  numberPerPage,
  setNumberPerPage,
  availableValues,
}: {
  numberPerPage: number;
  setNumberPerPage: (number: number) => void;
  availableValues: number[];
}) => {
  const { t } = useTranslation();

  const availableItems = availableValues.map((value) => ({
    id: value.toString(),
    label: t('global:pager.itemsPerPage', { count: value }),
  }));

  return (
    <StringDropdown<StringDropdownItem, false>
      availableItems={availableItems}
      item={{
        id: numberPerPage.toString(),
        label: t('global:pager.itemsPerPage', {
          count: numberPerPage,
        }),
      }}
      setItem={(item) => {
        if (item?.id) {
          setNumberPerPage(parseInt(item.id));
        }
      }}
      anchor={{ to: 'top', gap: 4 }}
    />
  );
};

export const Pager = ({
  numberPerPage,
  setNumberPerPage,
  pageInfo,
  previous,
  next,
  availableValues = [10, 20, 50],
}: {
  numberPerPage: number;
  setNumberPerPage: (number: number) => void;
  pageInfo: SupportedPageInfo;
  previous: () => void;
  next: () => void;
  availableValues?: number[];
}) => {
  const { t } = useTranslation();

  const hasPreviousPage = isPageInfo(pageInfo)
    ? pageInfo.hasPreviousPage
    : pageInfo.page > 1;

  const hasNextPage = isPageInfo(pageInfo)
    ? pageInfo.hasNextPage
    : pageInfo.page < pageInfo.pageCount;

  return (
    <div className="flex items-center justify-end gap-2">
      <div className="w-40">
        <NumberPerPageDropdown
          numberPerPage={numberPerPage}
          setNumberPerPage={setNumberPerPage}
          availableValues={availableValues}
        />
      </div>
      <button
        aria-label={t('global:pager.previous')}
        disabled={!hasPreviousPage}
        onClick={previous}
      >
        <ChevronLeftIcon />
      </button>
      {hasPage(pageInfo) && (
        <div>
          {t('global:pager.page', {
            count: pageInfo.page,
          })}
        </div>
      )}
      <button
        aria-label={t('global:pager.next')}
        disabled={!hasNextPage}
        onClick={next}
      >
        <ChevronRightIcon />
      </button>
    </div>
  );
};
